import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../../contexts/AuthContext';
import PageTitle from '../../../components/PageTitle/PageTitle';
import Table from '../../../components/Table/Table';
import Button from '../../../components/Button/Button';
import { Modal } from '../../../components/Modal/Modal';
import AlertModal from '../../../components/AlertModal/AlertModal';
import { extractApiError } from '../../../utils/normalizeApiError';

// Licenciamento de módulos (SuperAdmin) — liga/desliga o módulo por GRUPO e por HOTEL,
// com a precedência hotel>grupo visível. Revogar é soft (dados mantêm-se; Secção 9).
// Antes de desligar avisa se há conversas em curso (Secção 10).
const DEFAULT_MODULE = 'HUMAN_HANDOFF';
const MODULE_FALLBACK_LABEL = { HUMAN_HANDOFF: 'Atendimento Humano' };

function isActiveLicense(lic) {
  if (!lic || !lic.enabled) return false;
  if (lic.expires_at && new Date(lic.expires_at) <= new Date()) return false;
  return true;
}

function OnOff({ on }) {
  return (
    <span style={{ color: on ? '#1d7a45' : '#b42318', fontWeight: 600 }}>
      {on ? 'ON' : 'OFF'}
    </span>
  );
}

export default function Licensing() {
  const { axiosAuth, accessibleGroups, accessibleHotels } = useContext(AuthContext);
  const groups = useMemo(() => accessibleGroups || [], [accessibleGroups]);
  const hotels = useMemo(() => accessibleHotels || [], [accessibleHotels]);

  const [catalog, setCatalog] = useState([]);
  const [moduleCode, setModuleCode] = useState(DEFAULT_MODULE);
  const [licenses, setLicenses] = useState([]);
  const [groupFilter, setGroupFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState(''); // linha a ser alterada (desativa botões)
  const [alert, setAlert] = useState({ open: false, variant: 'danger', title: '', message: '' });

  // confirmação antes de desligar (Secção 10)
  const [confirm, setConfirm] = useState(null); // { kind, label, count, loadingCount, run }

  const showError = useCallback((title, err) => {
    setAlert({ open: true, variant: 'danger', title, message: extractApiError(err) });
  }, []);

  const moduleLabel = useMemo(() => {
    const m = catalog.find((c) => c.code === moduleCode);
    return m?.name || MODULE_FALLBACK_LABEL[moduleCode] || moduleCode;
  }, [catalog, moduleCode]);

  // catálogo de módulos (para nome + eventual seletor)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosAuth.get('/modules');
        setCatalog((data || []).filter((m) => m.active));
      } catch (err) {
        // não bloqueia a página; usa o fallback de label
      }
    })();
  }, [axiosAuth]);

  const loadLicenses = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosAuth.get('/modules/licenses', { params: { module_code: moduleCode } });
      setLicenses(data || []);
    } catch (err) {
      showError('Erro ao carregar licenças', err);
      setLicenses([]);
    } finally {
      setLoading(false);
    }
  }, [axiosAuth, moduleCode, showError]);

  useEffect(() => { loadLicenses(); }, [loadLicenses]);

  // índices por nível: hotel (hotel_id != null) e grupo (hotel_id == null)
  const groupLicByGroup = useMemo(() => {
    const m = {};
    licenses.forEach((l) => { if (l.hotel_id == null && l.group_id != null) m[l.group_id] = l; });
    return m;
  }, [licenses]);
  const hotelLicByHotel = useMemo(() => {
    const m = {};
    licenses.forEach((l) => { if (l.hotel_id != null) m[l.hotel_id] = l; });
    return m;
  }, [licenses]);

  // decisão efetiva de um hotel — espelha o backend (precedência hotel>grupo, sem fallback
  // quando existe registo de hotel)
  const effForHotel = useCallback((hotel) => {
    const hr = hotelLicByHotel[hotel.id];
    if (hr) return { licensed: isActiveLicense(hr), source: 'hotel', row: hr };
    const gr = groupLicByGroup[hotel.group_id];
    if (gr) return { licensed: isActiveLicense(gr), source: 'group', row: gr };
    return { licensed: false, source: null, row: null };
  }, [hotelLicByHotel, groupLicByGroup]);

  // conta conversas em curso (não fechadas) para um conjunto de hotéis
  const countActive = useCallback(async (hotelIds) => {
    let total = 0;
    await Promise.all(hotelIds.map(async (hid) => {
      try {
        const { data } = await axiosAuth.get('/human-handoff/admin/analytics', { params: { hotel_id: hid } });
        total += (data?.total || 0) - (data?.by_state?.closed || 0);
      } catch (err) { /* hotel sem licença/sessões — ignora */ }
    }));
    return total;
  }, [axiosAuth]);

  // ---- ações de licença -------------------------------------------------
  async function grant(params) {
    await axiosAuth.post('/modules/licenses', { module_code: moduleCode, enabled: true, ...params });
  }
  async function revokeById(id) {
    await axiosAuth.delete(`/modules/licenses/${id}`);
  }

  // GRUPO: ON = grant; OFF = revoke da linha de grupo (soft)
  async function setGroupLicense(group, on) {
    const key = `g:${group.id}`;
    setBusyKey(key);
    try {
      if (on) await grant({ group_id: group.id });
      else {
        const row = groupLicByGroup[group.id];
        if (row) await revokeById(row.id);
      }
      await loadLicenses();
    } catch (err) { showError('Erro ao alterar licença do grupo', err); }
    finally { setBusyKey(''); }
  }

  // HOTEL — controlo de 3 estados (mapeia diretamente o backend):
  //   'inherit' = sem registo de hotel (segue o grupo) → purge da exceção, se existir
  //   'on'      = exceção a forçar ON  (POST enabled=true)
  //   'off'     = exceção a forçar OFF (POST enabled=false)
  function hotelMode(hotel) {
    const row = hotelLicByHotel[hotel.id];
    if (!row) return 'inherit';
    return row.enabled ? 'on' : 'off';
  }
  async function setHotelMode(hotel, mode) {
    const key = `h:${hotel.id}`;
    setBusyKey(key);
    try {
      if (mode === 'inherit') {
        const row = hotelLicByHotel[hotel.id];
        if (row) await axiosAuth.delete(`/modules/licenses/${row.id}`, { params: { purge: true } });
      } else if (mode === 'on') {
        await grant({ hotel_id: hotel.id });
      } else {
        await axiosAuth.post('/modules/licenses', { module_code: moduleCode, hotel_id: hotel.id, enabled: false });
      }
      await loadLicenses();
    } catch (err) { showError('Erro ao alterar licença do hotel', err); }
    finally { setBusyKey(''); }
  }

  // estado efetivo que um hotel teria após aplicar um modo (para decidir se é um "desligar")
  function willBeOn(hotel, mode) {
    if (mode === 'on') return true;
    if (mode === 'off') return false;
    return isActiveLicense(groupLicByGroup[hotel.group_id]); // inherit → segue o grupo
  }

  // dispara ação com confirmação quando é para DESLIGAR algo que está ligado (Secção 10)
  function requestGroupToggle(group, currentlyOn) {
    if (!currentlyOn) return setGroupLicense(group, true);
    const hotelIds = hotels.filter((h) => h.group_id === group.id).map((h) => h.id);
    openConfirm('grupo', group.name, hotelIds, () => setGroupLicense(group, false));
  }
  function requestHotelMode(hotel, mode) {
    if (mode === hotelMode(hotel)) return; // já está nesse estado
    const wasOn = effForHotel(hotel).licensed;
    if (wasOn && !willBeOn(hotel, mode)) {
      openConfirm('hotel', hotel.name, [hotel.id], () => setHotelMode(hotel, mode));
    } else {
      setHotelMode(hotel, mode);
    }
  }

  function openConfirm(kind, label, hotelIds, run) {
    setConfirm({ kind, label, count: null, loadingCount: true, run });
    countActive(hotelIds).then((count) => {
      setConfirm((c) => (c ? { ...c, count, loadingCount: false } : c));
    });
  }
  async function confirmRun() {
    if (!confirm) return;
    const run = confirm.run;
    setConfirm(null);
    await run();
  }

  // ---- colunas ----------------------------------------------------------
  const groupColumns = useMemo(() => [
    { key: 'name', label: 'Grupo' },
    {
      key: 'state', label: `Licença ${moduleLabel}`,
      render: (g) => {
        const row = groupLicByGroup[g.id];
        const on = isActiveLicense(row);
        return (
          <span>
            <OnOff on={on} />
            {row?.expires_at && (
              <span style={{ color: '#b45309', fontSize: 12, marginLeft: 8 }}>
                até {new Date(row.expires_at).toLocaleDateString('pt-PT')}
                {row.reason === 'trial' ? ' (trial)' : ''}
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: 'actions', label: 'Ação',
      render: (g) => {
        const on = isActiveLicense(groupLicByGroup[g.id]);
        return (
          <Button
            type="button"
            variant={on ? 'secondary' : 'primary'}
            disabled={busyKey === `g:${g.id}`}
            onClick={() => requestGroupToggle(g, on)}
          >
            {busyKey === `g:${g.id}` ? '…' : on ? 'Desligar' : 'Ligar'}
          </Button>
        );
      },
    },
  ], [groupLicByGroup, moduleLabel, busyKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const groupNameById = useMemo(() => {
    const m = {}; groups.forEach((g) => { m[g.id] = g.name; }); return m;
  }, [groups]);

  const filteredHotels = useMemo(() => (
    groupFilter ? hotels.filter((h) => String(h.group_id) === String(groupFilter)) : hotels
  ), [hotels, groupFilter]);

  const hotelColumns = useMemo(() => [
    { key: 'name', label: 'Hotel' },
    { key: 'group', label: 'Grupo', render: (h) => groupNameById[h.group_id] || `grupo #${h.group_id}` },
    {
      key: 'state', label: 'Efetivo',
      render: (h) => {
        const eff = effForHotel(h);
        const srcLabel = eff.source === 'hotel' ? 'exceção de hotel' : eff.source === 'group' ? 'herdado do grupo' : 'sem registo';
        return (
          <span>
            <OnOff on={eff.licensed} />
            <span style={{ color: '#586664', fontSize: 12, marginLeft: 8 }}>({srcLabel})</span>
            {eff.source === 'hotel' && eff.row?.expires_at && (
              <span style={{ color: '#b45309', fontSize: 12, marginLeft: 8 }}>
                até {new Date(eff.row.expires_at).toLocaleDateString('pt-PT')}
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: 'actions', label: 'Definição do hotel',
      render: (h) => {
        const mode = hotelMode(h);
        const busy = busyKey === `h:${h.id}`;
        const opts = [
          { m: 'inherit', label: 'Herdar do grupo' },
          { m: 'on', label: 'Ligar' },
          { m: 'off', label: 'Desligar' },
        ];
        return (
          <div style={{ display: 'inline-flex', border: '1px solid #cdd8d6', borderRadius: 8, overflow: 'hidden' }}>
            {opts.map((o, i) => {
              const active = mode === o.m;
              return (
                <button
                  key={o.m}
                  type="button"
                  disabled={busy}
                  onClick={() => requestHotelMode(h, o.m)}
                  style={{
                    padding: '6px 12px', border: 'none', cursor: busy ? 'default' : 'pointer',
                    borderLeft: i ? '1px solid #cdd8d6' : 'none',
                    background: active ? (o.m === 'off' ? '#b42318' : o.m === 'on' ? '#1d7a45' : '#42607a') : '#f6f8f8',
                    color: active ? '#fff' : '#3a4644', fontWeight: active ? 700 : 500,
                    opacity: busy ? 0.6 : 1,
                  }}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        );
      },
    },
  ], [effForHotel, groupNameById, busyKey, hotelLicByHotel, groupLicByGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="licensing-page">
      {alert.open && (
        <AlertModal variant={alert.variant} title={alert.title} message={alert.message} onClose={() => setAlert({ ...alert, open: false })} />
      )}

      <PageTitle link="" title="Licenciamento de módulos" />

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '12px 0', flexWrap: 'wrap' }}>
        <label htmlFor="lic-module">Módulo</label>
        <select id="lic-module" value={moduleCode} onChange={(e) => setModuleCode(e.target.value)}>
          {(catalog.length ? catalog : [{ code: DEFAULT_MODULE, name: moduleLabel }]).map((m) => (
            <option key={m.code} value={m.code}>{m.name || m.code}</option>
          ))}
        </select>
        <span style={{ color: '#586664', fontSize: 13, maxWidth: 520 }}>
          O <strong>grupo</strong> é o interruptor geral. Cada hotel pode <strong>Herdar do grupo</strong> (segue o
          grupo) ou ter uma <strong>exceção</strong> (Ligar/Desligar) que se sobrepõe ao grupo. Tudo é reversível.
        </span>
        <div style={{ marginLeft: 'auto' }}>
          <Button type="button" variant="secondary" onClick={loadLicenses} disabled={loading}>Atualizar</Button>
        </div>
      </div>

      {loading && <p>A carregar…</p>}

      {!loading && (
        <>
          <h3 style={{ margin: '18px 0 8px' }}>Grupos</h3>
          <div className="container">
            <Table columns={groupColumns} data={groups} />
            {!groups.length && <p style={{ color: '#586664' }}>Sem grupos acessíveis.</p>}
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '22px 0 8px' }}>
            <h3 style={{ margin: 0 }}>Hotéis</h3>
            <label htmlFor="lic-gfilter" style={{ marginLeft: 12 }}>Filtrar por grupo</label>
            <select id="lic-gfilter" value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}>
              <option value="">Todos</option>
              {groups.map((g) => (<option key={g.id} value={g.id}>{g.name}</option>))}
            </select>
          </div>
          <div className="container">
            <Table columns={hotelColumns} data={filteredHotels} />
            {!filteredHotels.length && <p style={{ color: '#586664' }}>Sem hotéis para o filtro atual.</p>}
          </div>
        </>
      )}

      {/* Confirmação antes de desligar (Secção 10) */}
      {confirm && (
        <Modal
          isVisible={!!confirm}
          title={`Desligar ${moduleLabel} — ${confirm.kind === 'grupo' ? 'grupo' : 'hotel'} "${confirm.label}"`}
          onClose={() => setConfirm(null)}
          onSave={confirmRun}
          saveText="Desligar"
          disableActions={confirm.loadingCount}
        >
          <p>
            Vais revogar a licença deste {confirm.kind}. O acesso ao módulo é retirado, mas <strong>os
            dados e o histórico mantêm-se</strong> e podes voltar a ligar depois.
          </p>
          {confirm.loadingCount ? (
            <p style={{ color: '#586664' }}>A verificar conversas em curso…</p>
          ) : confirm.count > 0 ? (
            <p style={{ color: '#b42318', fontWeight: 600 }}>
              Existem {confirm.count} atendimento(s) em curso. As conversas já atribuídas continuam;
              a fila é encerrada pelo sistema.
            </p>
          ) : (
            <p style={{ color: '#1d7a45' }}>Não há conversas em curso.</p>
          )}
        </Modal>
      )}
    </div>
  );
}
